import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { isAdmin, isModerator } from '@/shared/helpers/isAdmin';
import { AutorizationResponseType } from '@/shared/types/app.types';
import { IObject } from '@/shared/types/bookingObjects.types.ts';
import { IPersonPass } from '@/shared/types/passes.types';

import { IUser } from '../types/user.types';

interface UserStore {
	user: IUser | null;
	session: AutorizationResponseType | null;
	passes: IPersonPass[];
	targetPersonId: number | null;
	isAdmin: boolean;
	isModerator: boolean;
	personalPlaces: IObject[];
	carPasses: { label: string; value: string }[];
	setUser: (user: UserStore['user']) => void;
	setSession: (session: UserStore['session']) => void;
	setPasses: (passes: UserStore['passes']) => void;
	setTargetPersonId: (targetPersonId: UserStore['targetPersonId']) => void;
	setIsAdmin: (isAdmin: UserStore['isAdmin']) => void;
	setIsModerator: (isModerator: UserStore['isModerator']) => void;
	setPersonalPlaces: (places: UserStore['personalPlaces']) => void;
	setCarPasses: (passes: UserStore['carPasses']) => void;
	reset: () => void;
}

const initialState = {
	user: null as UserStore['user'],
	session: null as UserStore['session'],
	passes: [] as IPersonPass[],
	targetPersonId: null as number | null,
	isAdmin: false,
	isModerator: false,
	personalPlaces: [] as IObject[],
	carPasses: [] as { label: string; value: string }[],
};

const useUserStore = create<UserStore>()(
	persist(
		(set) => ({
			...initialState,
			setUser: (user) => set(() => ({ user })),
			setSession: (session) =>
				set(() => ({
					session,
					isAdmin: session ? Boolean(isAdmin(session)) : false,
				})),
			setPasses: (passes) =>
				set(() => ({
					passes,
					isModerator: Boolean(isModerator(passes)),
				})),
			setTargetPersonId: (targetPersonId) => set(() => ({ targetPersonId })),
			setIsAdmin: (isAdmin) => set(() => ({ isAdmin })),
			setIsModerator: (isModerator) => set(() => ({ isModerator })),
			setPersonalPlaces: (personalPlaces) => set(() => ({ personalPlaces })),
			setCarPasses: (carPasses) => set(() => ({ carPasses })),
			reset: () => set(() => ({ ...initialState })),
		}),
		{
			name: 'user_new',
			partialize: ({ user, session, passes, personalPlaces, carPasses }) => ({
				user,
				session,
				passes,
				personalPlaces,
				carPasses,
			}),
			// Persist hydrate is async. Login/init can write the live session first;
			// never replace it with a stale or empty user_new blob.
			merge: (persisted, current) => {
				const persistedState = (persisted ?? {}) as Partial<UserStore>;
				if (current.session?.access_token) {
					return {
						...current,
						user: current.user ?? persistedState.user ?? null,
					};
				}
				return { ...current, ...persistedState };
			},
			onRehydrateStorage: () => (state) => {
				if (!state) {
					return;
				}

				// Login already populated the store — don't clobber it with a cold blob.
				if (state.session?.access_token && state.user?.id && state.targetPersonId != null) {
					return;
				}

				const rawUser = localStorage.getItem('user');
				const rawPerson = localStorage.getItem('person');
				const rawPasses = localStorage.getItem('passes');
				const session =
					state.session ??
					(rawUser && rawUser !== 'undefined'
						? (JSON.parse(rawUser) as AutorizationResponseType)
						: null);
				const persistedPasses = Array.isArray(state.passes) ? state.passes : [];
				const passes =
					persistedPasses.length > 0
						? persistedPasses
						: rawPasses && rawPasses !== 'undefined'
							? (JSON.parse(rawPasses) as IPersonPass[])
							: [];
				const person =
					state.user ??
					(rawPerson && rawPerson !== 'undefined' ? (JSON.parse(rawPerson) as IUser) : null);
				const targetPersonId = session?.user?.person_id ?? person?.id ?? null;

				state.setSession(session);
				state.setPasses(passes);
				state.setTargetPersonId(targetPersonId);
				if (person) {
					state.setUser(person);
				}
			},
		},
	),
);

export default useUserStore;
